import React, { useState, useEffect, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { Icon } from 'semantic-ui-react';
import { toast } from 'react-toastify';
import { getFunctions, httpsCallable } from 'firebase/functions';
import './BookingCard.css';
import userIcon from './images/logo.png';

const MessageOwner = ({ boatOwnerUID, messageCloseModal, bookingDetails, initialIsReporting = false, title }) => {
  const [ownerProfile, setOwnerProfile] = useState(null);
  const [message, setMessage] = useState('');
  const [subject, setSubject] = useState('');
  const [writeError, setWriteError] = useState('');
  const [isSending, setIsSending] = useState(false);
  const selectBoatState = useSelector((state) => state.selectBoat) || {};
  // Use selectBoat data only if we are in a booking context (bookingDetails provided).
  const selectBoat = bookingDetails ? selectBoatState : {};
  const currentUser = useSelector((state) => state.currentUser);
  const MAX_CHARS = 1000;
  const [sendCopy, setSendCopy] = useState(false);
  const [isReporting, setIsReporting] = useState(initialIsReporting);
  const [reportReason, setReportReason] = useState('');

  const formatStartTime = (time) => {
    if (!time) return '';
    const [hour, minute] = time.split(':');
    const h = parseInt(hour, 10);
    if (isNaN(h)) return time; // Fallback for invalid time format
    const ampm = h >= 12 ? 'PM' : 'AM';
    const formattedHour = h % 12 === 0 ? 12 : h % 12;
    return `${formattedHour}:${minute} ${ampm}`;
  };

  useEffect(() => {
    const fetchOwnerProfile = async () => {
      if (!boatOwnerUID) {
        return;
      }
      try {
        const functions = getFunctions();
        // DEFINITIVE FIX: Call the new, secure function that includes the recipient's email.
        const getMessageRecipientProfile = httpsCallable(functions, 'getMessageRecipientProfile');
        const result = await getMessageRecipientProfile({ userId: boatOwnerUID });
        if (result.data) {
          // The ownerProfile now contains only the public data returned from the function.
          setOwnerProfile(result.data);
        }
      } catch (error) {
        toast.error('Could not load owner profile.');
      }
    };

    fetchOwnerProfile();
  }, [boatOwnerUID]);

  useEffect(() => {
    // Pre-populate the message with context from the user's selections.
    if (bookingDetails?.fromDate) {
      let inquiryMessage = "Hi, I'm interested in your boat. ";
      if (bookingDetails.bookingTimeframe === 'Hourly') {
        inquiryMessage += `I'm looking at booking it for ${bookingDetails.hours} hour(s) on ${bookingDetails.fromDate} starting at ${bookingDetails.startTime}.`;
      } else { // Daily or "Per Trip"
        if (bookingDetails.fromDate === bookingDetails.toDate) {
          inquiryMessage += `I'm looking at booking it for the trip on ${bookingDetails.fromDate}.`;
        } else {
          inquiryMessage += `I'm looking at booking it from ${bookingDetails.fromDate} to ${bookingDetails.toDate}.`;
        }
      }

      if (bookingDetails.bookingType === 'Excursion') {
        inquiryMessage += ` For ${bookingDetails.quantity} guest(s).`;
      }

      // Add a placeholder for the user to continue their message.
      setMessage(inquiryMessage + '\n\n');
      setSubject(`Inquiry about: ${selectBoat.boatManufacturer} ${selectBoat.boatModel}`);
    } else {
      // Default message if no booking details are selected
      if (bookingDetails) {
        setMessage("Hi, I'm interested in your boat. ");
        setSubject(`Inquiry about: ${selectBoat.boatManufacturer} ${selectBoat.boatModel}`);
      } else {
        setMessage("Hi, I'm interested in your listing on BoatRBO. ");
        setSubject(`Inquiry about your BoatRBO listing`);
      }
    }
  }, [bookingDetails, selectBoat.boatManufacturer, selectBoat.boatModel]); // This runs when the modal opens and bookingDetails are populated.

  const handleSubmit = useCallback(async () => {
    if (!subject.trim() || !message.trim()) {
      setWriteError('Subject and message cannot be empty.');
      return;
    }

    if (!currentUser || (!selectBoat.ownerUID && !boatOwnerUID) || !ownerProfile) {
      setWriteError('User or boat data is missing.');
      return;
    }

    setIsSending(true);
    setWriteError('');

    const newMessage = {
      subject: subject,
      message: message,
      boatManufacturer: selectBoat.boatManufacturer || 'General',
      boatModel: selectBoat.boatModel || 'Inquiry',
      boatType: selectBoat.boatType || 'Store',
      boatOwnerUID: boatOwnerUID || selectBoat.ownerUID,
      customerUID: currentUser.id,
      sendCopyToSelf: sendCopy,
    };

    try {
      // DEFINITIVE FIX: Use the secure 'sendMessage' Cloud Function.
      const functions = getFunctions();
      const sendMessageFn = httpsCallable(functions, 'sendMessage');
      // The 'ownerProfile' state already contains the recipient's details, including email.
      await sendMessageFn({ messagePayload: newMessage, recipientProfile: ownerProfile });

      toast.success('Your message has been sent successfully!');
      setMessage('');
      messageCloseModal();
    } catch (error) {
      setWriteError(error.message);
      toast.error(`Failed to send message: ${error.message}`);
    } finally {
      setIsSending(false);
    }
  }, [message, subject, currentUser, selectBoat, ownerProfile, messageCloseModal, sendCopy, boatOwnerUID]);

  const handleReportSubmit = async () => {
    if (!reportReason.trim()) return;
    setIsSending(true);
    try {
      const functions = getFunctions();
      const reportUserFn = httpsCallable(functions, 'reportUser');
      await reportUserFn({ reportedUserId: boatOwnerUID, reason: reportReason, reporterUserId: currentUser.id });
      toast.success('User reported successfully.');
      setIsReporting(false);
      setReportReason('');
    } catch (error) {
      toast.error('Failed to report user.');
    } finally {
      setIsSending(false);
    }
  };

  const isVerifiedCaptain = ownerProfile?.captainLicenseVerified === true;
  return (
    <div className="select-boat-message-owner-container" style={{ background: 'white', marginTop: '30px', border: '1px solid #ccc', boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)', padding: '10px', borderRadius: '5px' }}>
      <div className="select-boat-message-owner-container-left">
        <div className="select-boat-message-owner-container-avatar">
          <img
            src={ownerProfile?.photoURL || userIcon}
            alt={ownerProfile?.displayName || 'Boat Owner'}
            className="dashboard-ash-profile-pic-img"
            style={{ width: '100px', height: 'auto', maxHeight: '100px', borderRadius: '50%' }}
          />
          {isVerifiedCaptain && (
            <div className="verified-badge-container" style={{ position: "absolute", background: "none", top: "0", left: "0", bottom: "unset", right: "unset", backgroundColor: "#37a1ec", borderRadius: "50%", lineHeight: "1", padding: "1px", margin: "110px 0px 0px 120px" }}
>
              <Icon name="anchor" color="blue" size="large" title="Verified Captain" />
              <svg width="24" stroke="#ffffffff" height="24" viewBox="0 0 50 42" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M42.51 8.01699C37.6524 3.7542 31.3942 0.983398 25 0.983398C18.6124 0.983398 12.3533 3.74912 7.49004 8.01689C2.87002 12.0714 0 17.0473 0 21.0029C0 24.2201 2.22617 26.995 6.44531 29.0557V32.7236C6.44531 35.8779 8.56777 38.6119 11.6067 39.3723C15.9883 40.4686 20.4934 41.0166 25 41.0166C29.5057 41.0166 34.0128 40.4684 38.3932 39.3723C41.4322 38.6119 43.5547 35.8779 43.5547 32.7236V29.0557C47.7738 26.995 50 24.2201 50 21.0029C50 17.0473 47.13 12.0714 42.51 8.01699ZM37.9191 37.4774C33.6933 38.535 29.3467 39.0637 25 39.0637C20.6533 39.0637 16.3066 38.535 12.0808 37.4775C9.91269 36.9351 8.39844 34.9802 8.39844 32.7236V32.2334H20.6055C21.1447 32.2334 21.582 31.7961 21.582 31.2568C21.582 30.7176 21.1447 30.2803 20.6055 30.2803H8.39844V26.374H41.6016V30.2803H29.3945C28.8553 30.2803 28.418 30.7176 28.418 31.2568C28.418 31.7961 28.8553 32.2334 29.3945 32.2334H41.6016V32.7236C41.6016 34.9802 40.0873 36.9351 37.9191 37.4774ZM43.5547 26.8545V25.3975C43.5547 24.8582 43.1174 24.4209 42.5781 24.4209H7.42188C6.88262 24.4209 6.44531 24.8582 6.44531 25.3975V26.8544C4.16113 25.5844 1.95312 23.668 1.95312 21.0029C1.95312 17.6445 4.63223 13.1234 8.77832 9.48506C13.5205 5.32344 19.4331 2.93652 25 2.93652C30.5669 2.93652 36.4795 5.32334 41.2217 9.48506C45.3678 13.1235 48.0469 17.6446 48.0469 21.0029C48.0469 23.6681 45.8389 25.5845 43.5547 26.8545Z" fill="white"></path></svg>          
            </div>
          )}          
        </div>
        {/* <div className="select-boat-message-owner-container-name">
          {ownerProfile?.displayName || selectBoat?.ownerDisplayName || 'Boat Owner'}
        </div> */}
        <div className="select-boat-message-owner-container-city-and-age">
          <p>{selectBoat?.boatManufacturer ? `${selectBoat.boatManufacturer} ${selectBoat.boatModel}` : ''}</p>
        </div>
      </div>
      <div className="select-boat-message-owner-container-right">
        <h1 className="select-boat-message-owner-enquiry">
          {isReporting ? 'Report User' : (title || 'Message the Captain')}
        </h1>
        <div className="select-boat-message-owner-departure">
          {bookingDetails && (
            <>
              <div>
                <h3 className="select-boat-message-owner-departure-text">
                  Departure Date
                </h3>
                <p className="select-boat-message-owner-departure-text-date">
                  {bookingDetails?.fromDate}
                </p>
              </div>

              {bookingDetails?.bookingTimeframe === 'Hourly' && (
                <div>
                  <h3 className="select-boat-message-owner-departure-text">Start Time</h3>
                  <p className="select-boat-message-owner-departure-text-date">{formatStartTime(bookingDetails?.startTime)}</p>
                </div>
              )}

              {/* Show Passengers for Excursions */}
              {bookingDetails?.bookingType === 'Excursion' && (
                <div>
                  <h3 className="select-boat-message-owner-departure-text">
                    Passengers
                  </h3>
                  <p className="select-boat-message-owner-departure-text-date">
                    {bookingDetails?.quantity}
                  </p>
                </div>
              )}

              {/* Show Hours for Hourly bookings */}
              {bookingDetails?.bookingTimeframe === 'Hourly' && (
                <div>
                  <h3 className="select-boat-message-owner-departure-text">
                    Hours
                  </h3>
                  <p className="select-boat-message-owner-departure-text-date">
                    {bookingDetails?.hours}
                  </p>
                </div>
              )}

              {/* Show Days for Daily bookings */}
              {bookingDetails?.bookingTimeframe === 'Daily' && (
                <div>
                  <h3 className="select-boat-message-owner-departure-text">
                    Days
                  </h3>
                  <p className="select-boat-message-owner-departure-text-date">
                    {bookingDetails?.bookingDays}
                  </p>
                </div>
              )}
            </>
          )}
        </div>
        
        {!isReporting && (
          <div style={{ marginTop: '17px', width: '80%', display: 'inline-block' }}>
            <p className="select-boat-message-owner-explain-text">
              {bookingDetails 
                ? "Leave a message and the captain will respond to you asap"
                : "Leave a message and the seller will respond to you asap"}
            </p>
          </div>
        )}

        {isReporting ? (
          <div className="select-boat-message-owner-input-container">
            <textarea
              className="select-boat-message-owner-input-name"
              placeholder="Please describe why you are reporting this user..."
              style={{ height: '135px', padding: '10px', width: '100%', border: '1px solid #ccc', boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)', borderRadius: '5px' }}
              value={reportReason}
              maxLength={MAX_CHARS}
              onChange={(e) => setReportReason(e.target.value)}
            />
            <div style={{ textAlign: 'right', fontSize: '0.9em', color: '#666', marginTop: '5px' }}>
              {reportReason.length}/{MAX_CHARS} characters
            </div>
            <div className="select-boat-message-owner-button-container">
              <div className="select-boat-message-owner-button-align">
                <button
                  className="select-boat-message-owner-send"
                  style={{ margin: '10px 2% 10px 5%', width: '40%', background: '#d9534f', border: '1px solid #ccc', color: 'white', borderRadius: '5px', padding: '10px' }}
                  onClick={handleReportSubmit}
                  disabled={isSending}
                >
                  {isSending ? 'Submitting...' : 'Submit Report'}
                </button>
                <button
                  className="select-boat-message-owner-cancel"
                  style={{ margin: '10px 5% 10px 2%', width: '40%', background: '#fff', border: '1px solid #ccc', color: '#333', borderRadius: '5px', padding: '10px' }}
                  onClick={() => setIsReporting(false)}
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        ) : (
          <>
            <div className="select-boat-message-owner-input-container">
              <input
                type="text"
                placeholder="Subject"
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                style={{ marginBottom: '10px', padding: '10px', width: '100%', border: '1px solid #ccc', boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)', borderRadius: '5px' }}
                required
              />
              <textarea
                className="select-boat-message-owner-input-name"
                type="text"
                placeholder="Enter your message here"
                style={{ height: '135px', padding: '10px', width: '100%', border: '1px solid #ccc', boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)', borderRadius: '5px' }}
                value={message}
                maxLength={MAX_CHARS}
                onChange={(event) => {
                  setMessage(event.target.value);
                  setWriteError('');
                }}
              />
              <div style={{ textAlign: 'right', fontSize: '0.9em', color: '#666', marginTop: '5px' }}>
                {message.length}/{MAX_CHARS} characters
              </div>
              <div style={{ marginTop: '10px', textAlign: 'left' }}>
                <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={sendCopy}
                    onChange={(e) => setSendCopy(e.target.checked)}
                    style={{ marginRight: '8px', width: 'auto' }}
                  />
                  <span style={{ color: '#666', fontSize: '0.9em' }}>Send a copy to my email</span>
                </label>
              </div>
            </div>
            <div className="select-boat-message-owner-button-container">
              <div className="select-boat-message-owner-button-align">            
                <button
                  className="select-boat-message-owner-send"
                  style={{ margin: '10px 2% 10px 5%', width: '40%', background: '#39a0ed', border: '1px solid #ccc', color: 'white', borderRadius: '5px', padding: '10px' }}
                  onClick={handleSubmit}
                  disabled={isSending}
                >
                  {isSending ? 'Sending...' : 'Send'}
                </button>
                <button
                  className="select-boat-message-owner-cancel"
                  style={{ margin: '10px 5% 10px 2%', width: '40%', background: '#39a0ed', border: '1px solid #ccc', color: 'white', borderRadius: '5px', padding: '10px' }}
                  onClick={messageCloseModal}
                >
                  Cancel
                </button>
              </div>
            </div>
            <div style={{ textAlign: 'center', marginTop: '5px' }}>
              <span 
                style={{ color: '#999', cursor: 'pointer', fontSize: '0.85em' }} 
                onClick={() => setIsReporting(true)}
              >
                <Icon name="flag" /> Report User
              </span>
            </div>
          </>
        )}

        {writeError && <p style={{ color: 'red' }}>{writeError}</p>}
      </div>
    </div>
  );
};

export default MessageOwner;
